import { useState } from "react";

import { IndicadoresDia } from "./IndicadoresDia";
import { formatarDecimal } from "../../lib/formato/decimal";
import type { DiaProjetadoOut } from "../../lib/projecao/tipos";

/*
 * Tabela da série projetada (FE-2 CP-B — T3, tabela-primeiro; CP-D — flags +
 * drill-down).
 *
 * Renderiza a série (`DiaProjetadoOut[]`) como tabela: data, entradas, saídas,
 * saldo e os indicadores das flags do dia (`IndicadoresDia`). TODO valor
 * monetário passa por `formatarDecimal` (string-based) — NUNCA `Number`/`toFixed`,
 * que perderiam precisão do `NUMERIC(18,2)` do wire.
 *
 * Drill-down: a célula da data é um `<button>`. HÁ DOIS MODOS, segundo a prop
 * opcional `aoAbrirComposicao`:
 *  - PRESENTE (visão EMPRESA, FE-8): clicar o dia chama `aoAbrirComposicao(data)`,
 *    que abre o `ModalComposicaoDia` com a composição itemizada (BE gap A). A
 *    linha-detalhe RASA NÃO é renderizada neste modo.
 *  - AUSENTE (visão GRUPO, CP-D): mantém o drill-down RASO — o botão expande/recolhe
 *    uma linha de detalhe com entradas/saídas do próprio dia DESTACADAS (sem
 *    endpoint extra — é detalhe da linha já carregada). Estado de expansão é
 *    client-state local: UM dia expandido por vez (`useState<string | null>` pela
 *    chave `data`). Acessível via `aria-expanded` (só faz sentido no modo raso).
 *
 * Sem lib de chart (T3 permanece). Reusada por `VisaoEmpresa` e `VisaoGrupo`: o
 * grupo segue com o raso (zero regressão), a empresa abre o modal itemizado.
 */

interface TabelaProjecaoProps {
  serie: DiaProjetadoOut[];
  /**
   * Quando presente (visão EMPRESA), clicar o dia abre o modal de composição
   * itemizada em vez do toggle raso. Ausente (visão GRUPO) → toggle raso.
   */
  aoAbrirComposicao?: (data: string) => void;
}

export function TabelaProjecao({
  serie,
  aoAbrirComposicao,
}: TabelaProjecaoProps): JSX.Element {
  // Dia atualmente expandido no modo RASO (chave = `data`); `null` = nenhum.
  // Irrelevante quando `aoAbrirComposicao` está presente (modo modal).
  const [expandido, setExpandido] = useState<string | null>(null);

  if (serie.length === 0) {
    return (
      <p className="font-corpo text-base text-off-black">
        Sem dias projetados no horizonte.
      </p>
    );
  }

  function alternar(data: string): void {
    setExpandido((atual) => (atual === data ? null : data));
  }

  return (
    <table className="w-full border-collapse font-corpo text-base text-off-black">
      <thead>
        <tr className="border-b border-platinum text-left font-subtitulo">
          <th className="px-2 py-1">Data</th>
          <th className="px-2 py-1 text-right">Entradas</th>
          <th className="px-2 py-1 text-right">Saídas</th>
          <th className="px-2 py-1 text-right">Saldo</th>
          <th className="px-2 py-1">Indicadores</th>
        </tr>
      </thead>
      <tbody>
        {serie.map((dia) => {
          const aberto = expandido === dia.data;
          return (
            <DiaLinhas
              key={dia.data}
              dia={dia}
              aberto={aberto}
              onAlternar={() => alternar(dia.data)}
              aoAbrirComposicao={aoAbrirComposicao}
            />
          );
        })}
      </tbody>
    </table>
  );
}

interface DiaLinhasProps {
  dia: DiaProjetadoOut;
  aberto: boolean;
  onAlternar: () => void;
  // Repassado da prop opcional da tabela; aceita `undefined` explícito porque o pai
  // sempre o passa (`exactOptionalPropertyTypes`). `undefined` = modo raso.
  aoAbrirComposicao: ((data: string) => void) | undefined;
}

/*
 * Linhas de um dia. No modo MODAL (`aoAbrirComposicao` presente) só há a
 * linha-resumo, cujo botão chama `aoAbrirComposicao(data)` — nenhuma linha-detalhe
 * rasa nem `aria-expanded`. No modo RASO (ausente) a linha-resumo faz toggle e a
 * linha-detalhe rasa aparece quando `aberto`.
 */
function DiaLinhas({
  dia,
  aberto,
  onAlternar,
  aoAbrirComposicao,
}: DiaLinhasProps): JSX.Element {
  const modoModal = aoAbrirComposicao !== undefined;
  return (
    <>
      <tr className="border-b border-platinum">
        <td className="px-2 py-1">
          <button
            type="button"
            onClick={
              modoModal ? () => aoAbrirComposicao(dia.data) : onAlternar
            }
            aria-expanded={modoModal ? undefined : aberto}
            className="font-corpo text-base text-off-black underline-offset-2 hover:underline"
          >
            {dia.data}
          </button>
        </td>
        <td className="px-2 py-1 text-right">{formatarDecimal(dia.entradas)}</td>
        <td className="px-2 py-1 text-right">{formatarDecimal(dia.saidas)}</td>
        <td className="px-2 py-1 text-right">{formatarDecimal(dia.saldo)}</td>
        <td className="px-2 py-1">
          <IndicadoresDia dia={dia} />
        </td>
      </tr>
      {!modoModal && aberto ? (
        <tr className="border-b border-platinum bg-snow">
          <td className="px-2 py-1" colSpan={5}>
            <dl className="flex flex-wrap gap-x-8 gap-y-1 font-corpo text-base text-off-black">
              <div>
                <dt className="font-subtitulo">Entradas do dia</dt>
                <dd>{formatarDecimal(dia.entradas)}</dd>
              </div>
              <div>
                <dt className="font-subtitulo">Saídas do dia</dt>
                <dd>{formatarDecimal(dia.saidas)}</dd>
              </div>
            </dl>
          </td>
        </tr>
      ) : null}
    </>
  );
}
